import React, { useState } from 'react';
import { useApp } from '../contexts/AppContext';
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { arrayMove, SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

// Componente para cada item da camada (sortable)
const SortableLayerItem = ({ layer, isSelected, isBaseLayer, onClick }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: layer.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      className={`
        group relative rounded-lg cursor-pointer
        transition-all duration-200
        ${isSelected
          ? 'bg-green-900/30'
          : 'bg-transparent hover:bg-gray-900/30'
        }
        ${isDragging ? 'shadow-xl z-50' : ''}
        flex items-center justify-center
      `}
      onClick={onClick}
    >
      {/* Thumbnail com drag handle sobreposto */}
      <div className="relative">
        {isBaseLayer ? (
          <div
            className="w-24 h-24 rounded-2xl border border-gray-300"
            style={{ backgroundColor: layer.backgroundColor || '#ffffff' }}
          />
        ) : (
          <img
            src={layer.src}
            alt={layer.name}
            className="w-24 h-24 object-cover rounded-2xl"
          />
        )}
        
        {/* Drag Handle sobreposto - centro do lado direito */}
        <div
          {...listeners}
          className="absolute top-1/2 right-1 -translate-y-1/2 cursor-move"
          onClick={(e) => e.stopPropagation()}
        >
          <svg
            className="w-4 h-4 text-white drop-shadow-lg opacity-70 hover:opacity-100 transition-opacity"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            strokeWidth={2.5}
          >
            <path strokeLinecap="round" strokeLinejoin="round" d="M4 8h16M4 16h16" />
          </svg>
        </div>

        {/* Selected Indicator */}
        {isSelected && (
          <div className="absolute bottom-1 left-1">
            <div className="w-2 h-2 bg-green-500 rounded-full shadow-lg"></div>
          </div>
        )}
      </div>
    </div>
  );
};

const LayerList = () => {
  const {
    uploadedImages,
    reorderImages,
    selectedImageId,
    setSelectedImage,
    setIsPaperPanelOpen,
  } = useApp();

  // Configurar sensores para drag and drop
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Handler para quando terminar de arrastar
  const handleDragEnd = (event) => {
    const { active, over } = event;

    if (active.id !== over.id) {
      // 🔧 IMPORTANTE: LayerList mostra ordem invertida (topo = frente)
      // Mas o array real tem índice 0 = fundo, último = frente
      // Então quando movemos no LayerList, precisamos inverter os índices

      const visualOrder = [...uploadedImages].reverse(); // Ordem visual (topo = frente)
      const oldVisualIndex = visualOrder.findIndex((item) => item.id === active.id);
      const newVisualIndex = visualOrder.findIndex((item) => item.id === over.id);

      // Mover na ordem visual
      const newVisualOrder = arrayMove(visualOrder, oldVisualIndex, newVisualIndex);

      // Reverter para ordem real (índice 0 = fundo)
      const newRealOrder = [...newVisualOrder].reverse();

      console.log('🔄 [LayerList] Reordenando:', {
        oldVisualIndex,
        newVisualIndex,
        visualOrder: newVisualOrder.map(img => img.name || img.id.toString().substring(0, 10)),
        realOrder: newRealOrder.map(img => img.name || img.id.toString().substring(0, 10))
      });

      reorderImages(newRealOrder);
    }
  };

  // Handler para clicar em uma camada
  const handleLayerClick = (layerId) => {
    // Se clicar em uma imagem, seleciona ela
    setSelectedImage(layerId);
    setIsPaperPanelOpen(false);
  };

  // 🔧 Ordem visual: topo da lista = frente do canvas
  // Inverter array para exibição (último índice do array = topo da lista)
  const visualOrder = [...uploadedImages].reverse();

  return (
    <div className="fixed right-6 top-1/2 -translate-y-1/2 z-20 w-[112px] max-h-[80vh] flex flex-col pointer-events-none animate-fadeIn">
      <div className="bg-black rounded-2xl shadow-xl overflow-hidden flex flex-col max-h-full pointer-events-auto">
        {/* Layer List - Scrollable */}
        <div className="flex-1 overflow-y-auto py-1 px-1 space-y-1">
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={visualOrder.map(img => img.id)}
              strategy={verticalListSortingStrategy}
            >
              {/* Uploaded Images - Ordem visual invertida (topo = frente) */}
              {visualOrder.map((image) => (
                <SortableLayerItem
                  key={image.id}
                  layer={image}
                  isSelected={selectedImageId === image.id}
                  isBaseLayer={false}
                  onClick={() => handleLayerClick(image.id)}
                />
              ))}
            </SortableContext>
          </DndContext>
        </div>
      </div>
    </div>
  );
};

export default LayerList;
